// SPDX-License-Identifier: Apache-2.0

// GmailSetupPanel (Iris). 4-step Gmail OAuth flow.
//
//   1. GCP project — instructions for the Desktop OAuth client.
//   2. upload     — file picker → POST /gmail/upload-client-secret.
//   3. authorize  — POST /gmail/start-auth → open auth_url + poll auth-status.
//   4. confirm    — connected email + tools-live message.
//
// Polling: every 1.5s, hard 5min watchdog. Stops on complete OR error.

import { Show, createSignal, onCleanup, type JSX } from "solid-js";
import { ActionRow, ErrorBox, Stepper, SuccessBox } from "./WizardStep";

const MCP_BASE = "http://localhost:5417/api/integrations";
const POLL_INTERVAL_MS = 1_500;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
const STEP_LABELS = ["GCP project", "upload secret", "authorize", "confirm"] as const;

export interface GmailSetupPanelProps {
  step: number;
  setStep: (n: number) => void;
  onDone: () => void;
}

export default function GmailSetupPanel(
  props: GmailSetupPanelProps
): JSX.Element {
  const [uState, setUState] = createSignal<"idle" | "pending" | "ok" | "error">("idle");
  const [uMsg, setUMsg] = createSignal("");
  const [aState, setAState] = createSignal<"idle" | "pending" | "complete" | "error">("idle");
  const [authUrl, setAuthUrl] = createSignal<string | null>(null);
  const [email, setEmail] = createSignal("");
  const [aErr, setAErr] = createSignal("");

  let pollTimer: number | null = null;
  let pollDeadline = 0;
  const stopPoll = () => {
    if (pollTimer != null) { clearInterval(pollTimer); pollTimer = null; }
  };
  onCleanup(stopPoll);

  const onFile = (e: Event) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const content = String(reader.result ?? "");
      setUState("pending"); setUMsg("");
      try {
        const res = await fetch(`${MCP_BASE}/gmail/upload-client-secret`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ json_content: content }),
        });
        const b = (await res.json()) as { ok: boolean; error?: string };
        if (b.ok) setUState("ok");
        else { setUState("error"); setUMsg(b.error ?? "upload failed"); }
      } catch (err) {
        setUState("error"); setUMsg((err as Error).message);
      }
    };
    reader.onerror = () => { setUState("error"); setUMsg("file read failed"); };
    reader.readAsText(file);
  };

  const startAuth = async () => {
    setAState("pending"); setAErr(""); setAuthUrl(null);
    try {
      const res = await fetch(`${MCP_BASE}/gmail/start-auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const b = (await res.json()) as { ok: boolean; auth_url?: string | null; error?: string };
      if (!b.ok) { setAState("error"); setAErr(b.error ?? "could not start auth"); return; }
      if (b.auth_url) {
        setAuthUrl(b.auth_url);
        try { window.open(b.auth_url, "_blank", "noopener,noreferrer"); } catch { /* popup blocked */ }
      }
      pollDeadline = Date.now() + POLL_TIMEOUT_MS;
      pollTimer = window.setInterval(() => void poll(), POLL_INTERVAL_MS);
    } catch (err) {
      setAState("error"); setAErr((err as Error).message);
    }
  };

  const poll = async () => {
    if (Date.now() > pollDeadline) {
      stopPoll(); setAState("error"); setAErr("auth flow timed out after 5 minutes");
      return;
    }
    try {
      const res = await fetch(`${MCP_BASE}/gmail/auth-status`);
      const b = (await res.json()) as {
        state: "pending" | "complete" | "error"; email?: string; error?: string;
      };
      if (b.state === "complete") {
        stopPoll(); setAState("complete"); setEmail(b.email ?? "");
        props.setStep(4); props.onDone();
      } else if (b.state === "error") {
        stopPoll(); setAState("error"); setAErr(b.error ?? "auth failed");
      }
    } catch { /* transient — keep polling until deadline */ }
  };

  const retry = () => { setAState("idle"); setAErr(""); setAuthUrl(null); };

  return (
    <section class="iris-wiz__panel" data-testid="comms-wizard-gmail-panel">
      <Stepper total={4} current={props.step} labels={STEP_LABELS} />

      <Show when={props.step === 1}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Create a Google Cloud OAuth client</h3>
          <p class="iris-wiz__copy">
            One-time, ~3 minutes. Pick <strong>Desktop app</strong> — Web
            clients won't work with Gmail-MCP.
          </p>
          <ol class="iris-wiz__instructions">
            <li>
              Open <a href="https://console.cloud.google.com/projectcreate" target="_blank" rel="noopener noreferrer">console.cloud.google.com</a> and create a project.
            </li>
            <li><strong>APIs & Services → Library</strong>, search <code>Gmail API</code>, click <strong>Enable</strong>.</li>
            <li><strong>OAuth consent screen</strong> → External, add yourself as a test user.</li>
            <li><strong>Credentials → Create credentials → OAuth client ID</strong> → <strong>Desktop app</strong> → Download JSON.</li>
          </ol>
          <ActionRow
            primaryLabel="I have the JSON →"
            primaryTestId="gmail-step1-next"
            onPrimary={() => props.setStep(2)}
          />
        </div>
      </Show>

      <Show when={props.step === 2}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Upload the OAuth client JSON</h3>
          <p class="iris-wiz__copy">
            We validate the shape, then write to{" "}
            <code>~/.gmail-mcp/gcp-oauth.keys.json</code> (<code>chmod 0600</code>).
          </p>
          <label
            class="iris-wiz__file"
            data-testid="gmail-file-dropzone"
            classList={{ "iris-wiz__file--ok": uState() === "ok" }}
          >
            <input
              type="file" accept="application/json,.json"
              data-testid="gmail-file-input" onChange={onFile}
            />
            <span class="iris-wiz__file-label">
              {uState() === "ok" ? "✓ uploaded — gcp-oauth.keys.json saved" : "click or drop client_secret_*.json"}
            </span>
          </label>
          <Show when={uState() === "error"}>
            <ErrorBox testId="gmail-upload-err">{uMsg()}</ErrorBox>
          </Show>
          <ActionRow
            onBack={() => props.setStep(1)}
            primaryLabel="continue →"
            primaryTestId="gmail-step2-next"
            primaryDisabled={uState() !== "ok"}
            onPrimary={() => props.setStep(3)}
          />
        </div>
      </Show>

      <Show when={props.step === 3}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Authorize tether-brain to read your mail</h3>
          <p class="iris-wiz__copy">
            We start the local Gmail-MCP auth and open Google's consent screen.
            Credentials land at <code>~/.gmail-mcp/credentials.json</code>{" "}
            (<code>chmod 0600</code>).
          </p>
          <ActionRow
            onBack={() => props.setStep(2)}
            primaryLabel={aState() === "pending" ? "waiting for Google…" : "open Google authorization"}
            primaryTestId="gmail-authorize-btn"
            primaryDisabled={aState() === "pending"}
            onPrimary={() => void startAuth()}
          />
          <Show when={aState() === "pending"}>
            <p class="iris-wiz__hint" data-testid="gmail-poll-status">
              Waiting for the consent flow to finish… (5min watchdog).
            </p>
            <Show when={authUrl()}>
              <p class="iris-wiz__copy">
                Browser didn't open?{" "}
                <a
                  href={authUrl() ?? "#"} target="_blank" rel="noopener noreferrer"
                  data-testid="gmail-auth-link"
                >click here to authorize</a>.
              </p>
            </Show>
          </Show>
          <Show when={aState() === "error"}>
            <ErrorBox testId="gmail-auth-err">
              <strong>Authorization failed:</strong> {aErr()}
            </ErrorBox>
            <ActionRow
              primaryLabel="retry"
              primaryTestId="gmail-retry-btn"
              onPrimary={retry}
            />
          </Show>
        </div>
      </Show>

      <Show when={props.step === 4}>
        <div class="iris-wiz__step-body">
          <h3 class="iris-wiz__h3">Connected.</h3>
          <Show
            when={email()}
            fallback={<p class="iris-wiz__copy">Gmail integration ready — credentials saved.</p>}
          >
            <p class="iris-wiz__copy">Authenticated as <code>{email()}</code>.</p>
          </Show>
          <SuccessBox testId="gmail-confirm-ok">
            <strong>10 mail.* tools now reachable from chat.</strong>
          </SuccessBox>
        </div>
      </Show>
    </section>
  );
}
